'use client';

import React, { useCallback, useEffect, useState } from 'react';

import type { LottieComponentProps } from 'lottie-react';
import styled, { keyframes } from 'styled-components';

import {
  getLoaderAnimationKind,
  isVideoContentType,
  type LoaderAnimationKind,
} from '@/lib/loaderAnimation';

/**
 * Portal-owned copy of shared-ui's AcCircularLoader (v0.5.383): the CSS
 * spinner path styled verbatim from its style.scss, plus the same
 * `animationUrl` swap for a publisher's processing animation (Lottie JSON or
 * WebM). The Lottie runtime is imported lazily, so publishers without an
 * animation never download it. Media-library URLs have no extension, so those
 * are fetched first and the Content-Type decides between video and Lottie. If
 * the asset cannot load, the spinner takes over.
 */

const ANIMATION_SIZE_PX = 96;

interface IAcCircularLoaderProps {
  text?: string;
  animationUrl?: string;
}

export const AcCircularLoader: React.FC<IAcCircularLoaderProps> = ({ text, animationUrl }) => {
  const [hasAnimationFailed, setHasAnimationFailed] = useState(false);
  const failAnimation = useCallback(() => setHasAnimationFailed(true), []);
  const showAnimation = Boolean(animationUrl) && !hasAnimationFailed;

  return (
    <StyledCircularLoader className="ac-circular-loader" data-testid="storeCircularLoader">
      {showAnimation && animationUrl ? (
        <CustomAnimation url={animationUrl} onError={failAnimation} />
      ) : (
        <span className="ac-circular-loader__spinner" aria-hidden />
      )}
      {/* The text belongs to the default spinner only; a publisher's animation stands on its own. */}
      {text && !showAnimation && <span>{text}</span>}
    </StyledCircularLoader>
  );
};

interface ICustomAnimationProps {
  url: string;
  onError: () => void;
}

const CustomAnimation: React.FC<ICustomAnimationProps> = ({ url, onError }) => {
  const [kind, setKind] = useState<LoaderAnimationKind>(() => getLoaderAnimationKind(url));
  const useVideo = useCallback(() => setKind('video'), []);

  if (kind === 'video') {
    return (
      <video
        className="ac-circular-loader__animation"
        data-testid="storeCircularLoader-animation"
        src={url}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        width={ANIMATION_SIZE_PX}
        height={ANIMATION_SIZE_PX}
        onError={onError}
        aria-hidden
      />
    );
  }
  return (
    <div className="ac-circular-loader__animation" data-testid="storeCircularLoader-animation" aria-hidden>
      <LottieAnimation url={url} onVideo={useVideo} onError={onError} />
    </div>
  );
};

type LottieComponent = React.ComponentType<LottieComponentProps>;

interface ILottieAnimationProps extends ICustomAnimationProps {
  /** The fetched asset turned out to be a video; the caller should play it. */
  onVideo: () => void;
}

const LottieAnimation: React.FC<ILottieAnimationProps> = ({ url, onVideo, onError }) => {
  const [ready, setReady] = useState<{ Lottie: LottieComponent; animationData: unknown } | null>(
    null
  );

  useEffect(() => {
    let isCurrent = true;
    fetch(url)
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to load loader animation: ${response.status}`);
        if (isVideoContentType(response.headers.get('content-type'))) return null;
        const [Lottie, animationData] = await Promise.all([
          import('lottie-react').then((module) => module.default),
          response.json(),
        ]);
        return { Lottie, animationData };
      })
      .then((loaded) => {
        if (!isCurrent) return;
        if (loaded === null) onVideo();
        else setReady(loaded);
      })
      .catch(() => {
        if (isCurrent) onError();
      });
    return () => {
      isCurrent = false;
    };
  }, [url, onVideo, onError]);

  if (!ready) return null;
  const { Lottie, animationData } = ready;
  return (
    <Lottie
      animationData={animationData}
      loop
      autoplay
      style={{ width: ANIMATION_SIZE_PX, height: ANIMATION_SIZE_PX }}
    />
  );
};

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const StyledCircularLoader = styled.div`
  font-family: var(--font-family);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 16px;

  .ac-circular-loader__spinner {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 4px solid rgba(255, 255, 255, 0.25);
    border-top-color: #ffffff;
    animation: ${spin} 0.8s linear infinite;
  }

  .ac-circular-loader__animation {
    width: ${ANIMATION_SIZE_PX}px;
    height: ${ANIMATION_SIZE_PX}px;
    object-fit: contain;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
